/* Table de données : une ligne d'outils (recherche + filtres repliés), des
   filtres par colonne posés dans l'en-tête, et les totaux chiffrés en pied.
   Au téléphone, les colonnes marquées `mobile: false` disparaissent et leur
   contenu remonte en sous-ligne sous la première colonne. */

import { Fragment, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { Empty } from "./primitives";
import { dateFr, entier, euro, pourcent } from "@/lib/format";

export interface Colonne<L> {
  cle: string;
  titre: string;
  /** Titre court affiché au téléphone. */
  court?: string;
  type?: "ref" | "num" | "euro" | "pct" | "date";
  classe?: string;
  /** Masque la colonne au téléphone. */
  mobile?: boolean;
  filtre?: "texte" | "select";
  total?: boolean;
  totalTitre?: string;
  agregat?: "moyenne";
  totalValeur?: (lignes: L[]) => number;
  rendu?: (l: L) => ReactNode;
  texte?: (l: L) => string;
  nombre?: (l: L) => number;
  /** Seconde ligne, visible au téléphone seulement. */
  sous?: (l: L) => ReactNode;
}

interface Props<L> {
  lignes: L[];
  colonnes: (Colonne<L> | null | false)[];
  cle: (l: L) => string;
  onClic?: (l: L, ev: React.MouseEvent) => void;
  nu?: boolean;
  chercher?: string;
}

/** Un clic sur un champ ne doit pas ouvrir la fiche de la ligne. */
export function dansUnChamp(ev: React.MouseEvent): boolean {
  const cible = ev.target as HTMLElement | null;
  return Boolean(cible?.closest("input, select, button, option, a, label"));
}

export function Table<L>({ lignes, colonnes, cle, onClic, nu, chercher }: Props<L>) {
  const cols = useMemo(() => colonnes.filter(Boolean) as Colonne<L>[], [colonnes]);
  const [f, setF] = useState<Record<string, string>>({});
  const [ouvert, setOuvert] = useState(false);
  const [q, setQ] = useState("");

  const brut = (l: L, c: Colonne<L>): string => {
    if (c.texte) return c.texte(l);
    const v = (l as Record<string, unknown>)[c.cle];
    return v == null ? "" : String(v);
  };
  const nombre = (l: L, c: Colonne<L>): number => {
    if (c.nombre) return c.nombre(l);
    const v = (l as Record<string, unknown>)[c.cle];
    return typeof v === "number" ? v : 0;
  };

  const visibles = lignes.filter((l) => {
    for (const c of cols) {
      const v = f[c.cle];
      if (!v) continue;
      const b = brut(l, c).toLowerCase();
      if (c.filtre === "select") {
        if (b !== v.toLowerCase()) return false;
      } else if (!b.includes(v.toLowerCase())) return false;
    }
    if (q) {
      const t = cols.map((c) => brut(l, c)).join(" ").toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const filtrables = cols.filter((c) => c.filtre);
  const totaux = cols.filter((c) => c.total);
  const nActifs = Object.values(f).filter(Boolean).length;
  // Une barre d'outils seulement quand la table est assez longue pour y chercher.
  const barre = lignes.length > 6;

  const options = (c: Colonne<L>): string[] => {
    const vus = new Set<string>();
    for (const l of lignes) {
      const b = brut(l, c);
      if (b) vus.add(b);
    }
    return [...vus].sort();
  };

  const classe = (c: Colonne<L>) => {
    if (c.type === "euro" || c.type === "num" || c.type === "pct") return "num";
    if (c.type === "ref") return "ref";
    return c.classe ?? "";
  };

  const contenu = (l: L, c: Colonne<L>): ReactNode => {
    if (c.rendu) return c.rendu(l);
    if (c.type === "euro") return euro(nombre(l, c));
    if (c.type === "num") return entier(nombre(l, c));
    if (c.type === "pct") return pourcent(nombre(l, c));
    if (c.type === "date") return dateFr(brut(l, c));
    const v = brut(l, c);
    return v === "" ? "—" : v;
  };

  const cellule = (l: L, c: Colonne<L>): ReactNode =>
    c.sous ? (
      <>
        {contenu(l, c)}
        <span className="sous">{c.sous(l)}</span>
      </>
    ) : (
      contenu(l, c)
    );

  const valeurTotale = (c: Colonne<L>): string => {
    const somme = visibles.reduce((s, l) => s + nombre(l, c), 0);
    const v = c.totalValeur
      ? c.totalValeur(visibles)
      : c.agregat === "moyenne"
        ? visibles.length
          ? somme / visibles.length
          : 0
        : somme;
    if (c.type === "euro") return euro(v);
    if (c.type === "pct") return pourcent(v);
    return entier(v);
  };

  const poser = (c: Colonne<L>, v: string) => setF((p) => ({ ...p, [c.cle]: v }));

  const champFiltre = (c: Colonne<L>): ReactNode => {
    if (!c.filtre) return null;
    if (c.filtre === "select") {
      return (
        <select
          aria-label={`Filtrer ${c.titre}`}
          value={f[c.cle] ?? ""}
          onChange={(e) => poser(c, e.target.value)}
        >
          <option value="">Tous</option>
          {options(c).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="search"
        aria-label={`Filtrer ${c.titre}`}
        value={f[c.cle] ?? ""}
        placeholder="—"
        onChange={(e) => poser(c, e.target.value)}
      />
    );
  };

  return (
    <div>
      {barre ? (
        <div className="tbar">
          <label className="tsearch">
            <Icon n="recherche" cls="" />
            <input
              type="search"
              value={q}
              placeholder={chercher ?? "Rechercher"}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          {filtrables.length ? (
            <button
              type="button"
              className={`btn btn-sm tfilt${ouvert ? " on" : ""}`}
              aria-expanded={ouvert}
              onClick={() => setOuvert(!ouvert)}
            >
              <Icon n="filtre" cls="" />
              <span className="t-long">Filtres</span>
              {nActifs ? <span className="n-actifs">{nActifs}</span> : null}
            </button>
          ) : null}
          {nActifs || q ? (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                setF({});
                setQ("");
              }}
            >
              Effacer
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={`card${nu ? " nu" : ""}`}>
        {visibles.length === 0 ? (
          <Empty titre="Aucune ligne" detail="Aucune ligne ne correspond aux filtres." />
        ) : (
          <div className="tablewrap">
            <table className="tbl">
              <thead>
                <tr>
                  {cols.map((c, i) => (
                    <th
                      key={c.cle}
                      className={
                        (classe(c) === "num" ? "num " : "") +
                        (i === 0 ? "pick " : "") +
                        (i === cols.length - 1 ? "last " : "") +
                        (c.mobile === false ? "c-opt" : "")
                      }
                    >
                      {c.court ? (
                        <Fragment>
                          <span className="t-long">{c.titre}</span>
                          <span className="t-court">{c.court}</span>
                        </Fragment>
                      ) : (
                        c.titre
                      )}
                    </th>
                  ))}
                </tr>
                {ouvert && filtrables.length ? (
                  <tr className="frow">
                    {cols.map((c, i) => (
                      <th
                        key={c.cle}
                        className={
                          (i === 0 ? "pick " : "") +
                          (i === cols.length - 1 ? "last " : "") +
                          (c.mobile === false ? "c-opt" : "")
                        }
                      >
                        {champFiltre(c)}
                      </th>
                    ))}
                  </tr>
                ) : null}
              </thead>
              <tbody>
                {visibles.map((l) => (
                  <tr
                    key={cle(l)}
                    className={onClic ? "clickable" : undefined}
                    onClick={onClic ? (ev) => onClic(l, ev) : undefined}
                  >
                    {cols.map((c, i) => (
                      <td
                        key={c.cle}
                        data-l={c.court ?? c.titre}
                        className={
                          (classe(c) ? `${classe(c)} ` : "") +
                          (i === 0 ? "pick head " : "") +
                          (i === cols.length - 1 ? "last " : "") +
                          (c.mobile === false ? "c-opt" : "")
                        }
                      >
                        {cellule(l, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totaux.length ? (
          <div className="table-totaux">
            <span className="n">
              {visibles.length === lignes.length
                ? `${visibles.length} ${visibles.length > 1 ? "lignes" : "ligne"}`
                : `${visibles.length} sur ${lignes.length} lignes`}
            </span>
            {totaux.map((c) => (
              <span key={c.cle}>
                <b>{c.totalTitre ?? c.titre}</b>
                <span className="v">{valeurTotale(c)}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
